/**
 * Flight data feeds: the live channel from an airline's or an airport's own
 * systems (SITA, Amadeus) into the flight record — IATA AIDX, Type B (MVT,
 * LDM, ASM, SSM) and mapped JSON, pushed to the platform or fetched by it.
 *
 * The platform decides everything here (movements/feeds.py); the screen shows
 * what it decided. These are its shapes under the web app's names.
 */

export type FeedKind = "aidx" | "typeb" | "json";
export type FeedTransport = "push" | "https" | "imap";
export type FeedState = "live" | "quiet" | "failing" | "off";
export type FeedOutcome = "taken" | "held" | "stored" | "duplicate" | "refused";

export const OUTCOMES: readonly FeedOutcome[] = ["taken", "held", "stored", "duplicate", "refused"];

export const OUTCOME_LABEL: Record<FeedOutcome, string> = {
  taken: "Taken",
  held: "Held",
  stored: "Stored",
  duplicate: "Duplicate",
  refused: "Refused",
};

export const STATE_LABEL: Record<FeedState, string> = {
  live: "Live",
  quiet: "Quiet",
  failing: "Failing",
  off: "Off",
};

export const KIND_LABEL: Record<FeedKind, string> = {
  aidx: "IATA AIDX (XML)",
  typeb: "Type B (MVT, LDM, ASM, SSM)",
  json: "JSON, mapped",
};

export const TRANSPORT_LABEL: Record<FeedTransport, string> = {
  push: "Pushed to us",
  https: "Fetched over HTTPS",
  imap: "Read from a mailbox (IMAP)",
};

export interface FeedStatus {
  state: FeedState;
  why?: string;
  lastMessageAt?: string;
  /** seconds since the last message arrived */
  lagSeconds?: number;
  /** how far behind the source's own message time we receive, on average over the hour */
  sourceLagSeconds?: number;
  perHour: number;
  last24h: number;
  outcomes24h: Record<FeedOutcome, number>;
  refusals24h: { reason: string; count: number }[];
}

export interface FeedAuth {
  kind: "none" | "api_key" | "oauth";
  header?: string;
  prefix?: string;
  tokenUrl?: string;
  clientId?: string;
  scope?: string;
}

/** Settings only. A secret is NAMED here (an SLT_FEED_… variable) and never held. */
export interface FeedConfig {
  url?: string;
  cursorParam?: string;
  auth?: FeedAuth;
  secretRefs?: Record<string, string>;
  imap?: { host: string; port?: number; user: string; folder?: string };
  mapping?: { records?: string; id?: string; cursor?: string; fields: Record<string, string> };
  pollSeconds?: number;
  quietMinutes?: number;
  /** may the feed add a flight the schedule does not have? */
  createUnknown?: boolean;
}

export interface FlightFeed {
  id: string;
  key: string;
  name: string;
  kind: FeedKind;
  transport: FeedTransport;
  enabled: boolean;
  config: FeedConfig;
  /** each named secret, and whether this deployment has it — never its value */
  secrets: Record<string, { name: string; set: boolean }>;
  /** where a pushing source posts, for a push feed */
  pushPath?: string;
  lastPollAt?: string;
  lastOkAt?: string;
  lastError?: string;
  status: FeedStatus;
}

export interface FeedMessage {
  id: string;
  feedId?: string;
  feed?: string;
  kind: string;
  channel: string;
  outcome: FeedOutcome;
  reason?: string;
  raw: string;
  envelope?: Record<string, unknown> | null;
  /** elements or lines nothing reads */
  ignored: string[];
  changes: string[];
  movementId?: string;
  duplicateOf?: string;
  replayOf?: string;
  sentAt?: string;
  receivedAt: string;
}

export interface FeedResult {
  index: number;
  outcome: FeedOutcome;
  reason?: string;
  movement?: string;
  changed?: string[];
  ignored?: string[];
  unmapped?: string[];
  messageId?: string;
}

export interface FeedTestResult {
  ok: boolean;
  error?: string;
  note?: string;
  secrets: Record<string, { name: string; set: boolean }>;
  results: FeedResult[];
}

export interface FeedDraft {
  key: string;
  name: string;
  kind: FeedKind;
  transport: FeedTransport;
  enabled: boolean;
  config: FeedConfig;
}
